/**
 * The shared selection: how you set it, and everything it implies.
 *
 * The pane *is* the editor. Every figure it prints is also the control that
 * sets it — type a mutagen value, an evolution, a flask count or a threat
 * budget and the range moves to match, on all four charts. The explicit
 * range-tool form is still there behind a toggle for the cases where you would
 * rather fill in two fields than edit two numbers.
 *
 * Everything reported here is exact: the endpoints run the real feeding loop at
 * those mutagen values rather than reading interpolated chart samples.
 *
 * This module also owns the difficulty control, because difficulty only ever
 * rescales flasks into mutagen; it belongs next to the flask costs and nowhere
 * else.
 */

import { useState, type FormEvent, type ReactNode } from 'react';

import {
  DIFFICULTIES,
  SCIENCE,
  SCIENCE_ORDER,
  TIER_COLOURS,
  UNIT_TIERS,
  formatCompact,
  formatEvo,
  formatFlasks,
  formatMultiplier,
  formatPercent,
  fromDisplayMutagen,
  type FlaskBasket,
  mutagenForThreat,
  mutagenToFlasks,
  mutagenToReachEvo,
  summariseRange,
  toDisplayMutagen,
  type RangeSummary,
  type Ruleset,
  type ScienceKey,
  type SeriesParams,
  type TierWeights,
  type UnitClass,
} from '../model';
import { BranchMark } from './ChartFrame';
import { FlaskMixDialog } from './FlaskMixDialog';
import { Info } from './Info';
import { useLab, type BranchView } from './LabContext';
import {
  EditableNumber,
  Field,
  NumberField,
  SegmentedControl,
  Transition,
  type SegmentedOption,
} from './controls';

export function SelectionModule() {
  const { views, comparing, selection, params, zoomToSelection, setSelection } = useLab();
  const [showTools, setShowTools] = useState(false);

  // With nothing selected the pane still edits: it stands in for a point at
  // zero, so typing any figure creates a real selection from scratch.
  const primarySummary = views[0].selectionSummary ?? summariseZero(params);
  const isRange = primarySummary.mutagen > 0;

  return (
    <section className="module selection-module">
      <header className="module-head">
        <h2>Selection</h2>
        <span className="module-hint">click any value to edit it</span>
        <Info title="Selection" align="right">
          <p>
            Drag across any chart for a range, click one for a single point, or type over any figure
            below — every number in this pane is also the control that sets it. The value and its
            magnitude (<code>k</code>, <code>M</code>) are separate controls, so editing the number
            cannot wipe the scale.
          </p>
          <p>
            <b>How edits move the range.</b> An endpoint — mutagen, evolution or passive threat —
            lands exactly where you put it; if that would invert the range, the other end is pulled
            to meet it and the selection becomes a point. A <b>flask count</b> or a{' '}
            <b>threat budget</b> describes how much is fed, so it holds the start and moves only the
            end.
          </p>
          <p>
            <b>Over selection</b> is the difference of the cumulative threat curve — the threat that
            slice of mutagen generates. <b>As one send</b> feeds the same slice in a single action
            from the range's own starting evolution; the few-percent gap between them is the game's
            coarser integration step for one large send, a real property of the implementation
            rather than a rounding artefact.
          </p>
          <p>
            <b>Difficulty</b> sits with the flask costs because that is all it does: it rescales
            flasks into mutagen, never mutagen into evolution. No curve moves when you change it.
          </p>
          {comparing ? (
            <p>
              <b>One pane per branch.</b> The selection itself is shared — it is a range of mutagen,
              and mutagen means the same thing everywhere — so the <b>Mutagen</b> row and the flask
              costs are deliberately identical in both. They are the input the comparison holds
              fixed; everything below them is what each branch does with it. Editing an evolution
              or a threat figure in a pane solves it with <i>that</i> branch's curve, which moves
              the shared range and so moves the other pane too.
            </p>
          ) : null}
        </Info>
        <div className="module-actions">
          <button
            type="button"
            aria-expanded={showTools}
            onClick={() => setShowTools((open) => !open)}
          >
            range tools
          </button>
          <button type="button" disabled={!isRange} onClick={zoomToSelection}>
            zoom to
          </button>
          <button type="button" disabled={!selection} onClick={() => setSelection(null)}>
            clear
          </button>
        </div>
      </header>

      {showTools ? <SelectionControls summary={primarySummary} /> : null}

      {views.map((view, index) => (
        <BranchPane
          key={view.ruleset.id}
          view={view}
          comparing={comparing}
          // Difficulty is one global value; two selects driving it would just
          // be two ways to change the same number sitting next to each other.
          showDifficulty={index === 0}
        />
      ))}
    </section>
  );
}

/**
 * Everything the selection implies, for one branch.
 *
 * While comparing there is one of these per branch, stacked. The selection they
 * read is shared — it is a mutagen range, which means the same thing on every
 * branch — so each pane differs only in what its own equations make of it.
 */
function BranchPane({
  view,
  comparing,
  showDifficulty,
}: {
  view: BranchView;
  comparing: boolean;
  showDifficulty: boolean;
}) {
  const { difficulty, unitClass, setSelection, ensureVisible } = useLab();
  const { ruleset, params } = view;

  const summary = view.selectionSummary ?? summariseZero(params);
  const isRange = summary.mutagen > 0;

  /*
   * Edit policy. Two kinds of edit, and each one has a single rule:
   *
   *   Endpoint edits (mutagen, evolution, passive threat) — the value you type
   *   lands on the endpoint you typed it into, exactly. If that would invert
   *   the range, the *other* endpoint is pulled to meet it and the selection
   *   collapses to a point there. It never silently becomes the opposite
   *   endpoint, which is what made this confusing before.
   *
   *   Extent edits (flask counts, threat budget) — these describe how much is
   *   fed, so the start is held and only the end moves.
   *
   * The figure is read through this pane's branch, but what it sets is the one
   * shared selection — so typing 200% into one pane moves both.
   */
  const apply = (from: number, to: number) => {
    if (!Number.isFinite(from) || !Number.isFinite(to) || from < 0 || to < 0) return;
    ensureVisible(Math.max(from, to));
    setSelection([from, to]);
  };
  const setStart = (mutagen: number) => apply(mutagen, Math.max(mutagen, summary.to.mutagen));
  const setEnd = (mutagen: number) => apply(Math.min(mutagen, summary.from.mutagen), mutagen);
  const setExtent = (mutagen: number) => apply(summary.from.mutagen, mutagen);

  return (
    <div className="selection-branch">
      {comparing ? (
        <div className="selection-branch-head">
          <BranchMark dash={view.dash} />
          <b>{ruleset.label}</b>
          <code>{ruleset.branch}</code>
        </div>
      ) : null}
      <div className="selection-grid">
        <ImpactGroup
          summary={summary}
          isRange={isRange}
          ruleset={ruleset}
          setStart={setStart}
          setEnd={setEnd}
        />
        <FlaskGroup
          summary={summary}
          difficulty={difficulty}
          showDifficulty={showDifficulty}
          setExtent={setExtent}
        />
        <ThreatGroup
          summary={summary}
          isRange={isRange}
          ruleset={ruleset}
          params={params}
          setExtent={setExtent}
        />
        <StrengthGroup summary={summary} isRange={isRange} unitClass={unitClass} />
        <MixGroup summary={summary} isRange={isRange} />
      </div>
    </div>
  );
}

/** The stand-in summary used when nothing is selected: a point at mutagen 0. */
function summariseZero(params: SeriesParams): RangeSummary {
  return summariseRange(0, 0, params);
}

/* ------------------------------------------------------------------ groups */

/** Where the range starts, where it ends, and what it moved. */
function ImpactGroup({
  summary,
  isRange,
  ruleset,
  setStart,
  setEnd,
}: {
  summary: RangeSummary;
  isRange: boolean;
  ruleset: Ruleset;
  setStart: (mutagen: number) => void;
  setEnd: (mutagen: number) => void;
}) {
  const { from, to } = summary;

  return (
    <Group title="Impact">
      <Row
        label="Mutagen"
        value={
          <Span
            isRange={isRange}
            delta={`+${formatCompact(toDisplayMutagen(summary.mutagen), 1)}`}
            from={
              <EditableNumber
                label="Mutagen at start"
                display={formatCompact(toDisplayMutagen(from.mutagen), 1)}
                onCommit={(value) => setStart(fromDisplayMutagen(value))}
              />
            }
            to={
              <EditableNumber
                label="Mutagen at end"
                display={formatCompact(toDisplayMutagen(to.mutagen), 1)}
                onCommit={(value) => setEnd(fromDisplayMutagen(value))}
              />
            }
          />
        }
      />
      <Row
        label="Evolution"
        value={
          <Span
            isRange={isRange}
            delta={`+${(summary.evoGain * 100).toFixed(1)}`}
            from={
              <EditableNumber
                label="Evolution at start"
                display={formatEvo(from.evo)}
                max={1200}
                scalable={false}
                suffix="%"
                onCommit={(value) => setStart(mutagenToReachEvo(ruleset, value / 100, 0))}
              />
            }
            to={
              <EditableNumber
                label="Evolution at end"
                display={formatEvo(to.evo)}
                max={1200}
                scalable={false}
                suffix="%"
                onCommit={(value) => setEnd(mutagenToReachEvo(ruleset, value / 100, 0))}
              />
            }
          />
        }
      />
      <Row
        label="Passive / min"
        value={
          <Span
            isRange={isRange}
            delta={`+${formatCompact(summary.threatPerMinuteGain, 1)}`}
            from={<b>{formatCompact(from.threatPerMinute, 1)}</b>}
            to={
              <EditableNumber
                label="Passive threat at end"
                display={formatCompact(to.threatPerMinute, 1)}
                // Live solves an evolution for the income; the test branch
                // solves a mutagen directly, since income is linear in it.
                onCommit={(value) => setEnd(ruleset.mutagenForPassivePerMinute(value))}
              />
            }
          />
        }
      />
    </Group>
  );
}

/**
 * What the range costs, in every science. Editing a count is the inline form of
 * "send this much from the start point".
 */
function FlaskGroup({
  summary,
  difficulty,
  showDifficulty,
  setExtent,
}: {
  summary: RangeSummary;
  difficulty: number;
  /** False on every pane but the first: difficulty is one global value. */
  showDifficulty: boolean;
  setExtent: (mutagen: number) => void;
}) {
  const [mixing, setMixing] = useState(false);
  // Held here rather than in the dialog so a mix survives closing it.
  const [basket, setBasket] = useState<FlaskBasket>({});

  return (
    <Group title="Cost in flasks" control={showDifficulty ? <DifficultyControl /> : undefined}>
      <div className="flask-list">
        {SCIENCE_ORDER.map((key) => {
          const flasks = mutagenToFlasks(summary.mutagen, key, difficulty);
          return (
            <span className="flask-item" key={key}>
              <span className="swatch" style={{ background: SCIENCE[key].hex }} />
              <span className="flask-name">{SCIENCE[key].colour}</span>
              <EditableNumber
                label={`${SCIENCE[key].colour} flasks`}
                display={formatFlasks(flasks)}
                onCommit={(count) =>
                  setExtent(summary.from.mutagen + SCIENCE[key].value * difficulty * count)
                }
              />
            </span>
          );
        })}

        {/* An eighth cell in the same grid: the mixed-science send. */}
        <button type="button" className="ghost flask-mix-button" onClick={() => setMixing(true)}>
          custom mix…
        </button>
      </div>

      {mixing ? (
        <FlaskMixDialog
          difficulty={difficulty}
          basket={basket}
          onBasketChange={setBasket}
          onApply={(mutagen) => setExtent(summary.from.mutagen + mutagen)}
          onClose={() => setMixing(false)}
        />
      ) : null}
    </Group>
  );
}

function DifficultyControl() {
  const { difficultyIndex, difficulty, setDifficultyIndex, setCustomDifficulty } = useLab();

  return (
    <span className="difficulty-control">
      <select
        aria-label="Difficulty"
        value={difficultyIndex === null ? 'custom' : String(difficultyIndex)}
        onChange={(event) => {
          const value = event.target.value;
          setDifficultyIndex(value === 'custom' ? null : Number(value));
        }}
      >
        {DIFFICULTIES.map((entry) => (
          <option key={entry.index} value={entry.index}>
            {entry.shortName} · {formatPercent(entry.value, 0)}
          </option>
        ))}
        <option value="custom">Custom</option>
      </select>
      {difficultyIndex === null ? (
        <NumberField
          value={Number((difficulty * 100).toFixed(1))}
          onChange={(value) => setCustomDifficulty(value / 100)}
          min={1}
          max={10_000}
          step={5}
          suffix="%"
        />
      ) : null}
    </span>
  );
}

function ThreatGroup({
  summary,
  isRange,
  ruleset,
  params,
  setExtent,
}: {
  summary: RangeSummary;
  isRange: boolean;
  ruleset: Ruleset;
  /** This pane's branch, so the threat inversion solves the right curve. */
  params: SeriesParams;
  setExtent: (mutagen: number) => void;
}) {
  // Every figure in this group is zero on a branch without instant threat, and
  // the threat-budget field would be an editor that cannot move anything. Say
  // what happened instead of printing three zeroes and a dead control.
  if (!ruleset.hasInstantThreat) {
    return (
      <Group title="Instant threat">
        <div className="sel-note">
          <p>
            Removed on <code>{ruleset.branch}</code>.
          </p>
          <p>
            <code>threat_increase = 0</code> for every send, so a send's whole effect is the
            evolution and the income it leaves behind. <b>Passive / min</b> above carries it.
          </p>
        </div>
      </Group>
    );
  }

  return (
    <Group title="Instant threat">
      <Row
        label="Over selection"
        strong
        tone="warn"
        value={
          <EditableNumber
            label="Instant threat over selection"
            display={formatCompact(summary.instantThreat, 1)}
            onCommit={(target) => setExtent(mutagenForThreat(summary.from.mutagen, target, params))}
          />
        }
      />
      {isRange ? (
        <Row label="As one send" value={formatCompact(summary.instantThreatSingleSend, 1)} />
      ) : null}
      <Row
        label="Cumulative"
        value={
          <Span
            isRange={isRange}
            from={<b>{formatCompact(summary.from.instantThreat, 1)}</b>}
            to={<b>{formatCompact(summary.to.instantThreat, 1)}</b>}
          />
        }
      />
    </Group>
  );
}

function StrengthGroup({
  summary,
  isRange,
  unitClass,
}: {
  summary: RangeSummary;
  isRange: boolean;
  unitClass: UnitClass;
}) {
  const { from, to } = summary;
  const modsFrom = unitClass === 'boss' ? from.boss : from.normal;
  const modsTo = unitClass === 'boss' ? to.boss : to.normal;

  const times = (value: number) => formatMultiplier(value, 1);
  const pair = (a: string, b: string) => (
    <Span isRange={isRange} from={<b>{a}</b>} to={<b>{b}</b>} />
  );

  return (
    <Group title={`Strength — ${unitClass}`}>
      <Row label="Health" value={pair(times(modsFrom.health), times(modsTo.health))} />
      <Row label="Damage" value={pair(times(modsFrom.damage), times(modsTo.damage))} />
      <Row label="Attack speed" value={pair(times(modsFrom.speed), times(modsTo.speed))} />
      {/* Group cap and boss attempts are one wave-shape figure; keep it to a line. */}
      <Row
        label="Max biters / bosses"
        value={pair(
          `${from.groupSize} / ${from.bossAttempts}`,
          `${to.groupSize} / ${to.bossAttempts}`,
        )}
      />
    </Group>
  );
}

function MixGroup({ summary, isRange }: { summary: RangeSummary; isRange: boolean }) {
  return (
    <Group title="Unit mix">
      {/* Says what each column is: the left figure is the evolution, not a share. */}
      <div className="mix-head">
        <span>evo</span>
        <span>share of spawns</span>
      </div>
      <div className="mix-pair">
        {isRange ? (
          <CompositionBar
            label={formatEvo(summary.from.evo)}
            composition={summary.from.composition}
          />
        ) : null}
        <CompositionBar label={formatEvo(summary.to.evo)} composition={summary.to.composition} />
      </div>
      <div className="mix-legend">
        {UNIT_TIERS.map((tier) => (
          <span key={tier}>
            <i style={{ background: TIER_COLOURS[tier] }} />
            {tier}
          </span>
        ))}
      </div>
    </Group>
  );
}

/* ------------------------------------------------------- the explicit form */

type Mode = 'mutagen' | 'evo' | 'threat' | 'science';
type StartUnit = 'mutagen' | 'evo';

const MODES: SegmentedOption<Mode>[] = [
  { value: 'mutagen', label: 'Mutagen range', title: 'From and to, in mutagen' },
  { value: 'evo', label: 'Evo range', title: 'From and to, in evolution' },
  { value: 'threat', label: 'Threat target', title: 'From a start point, until N instant threat' },
  { value: 'science', label: 'Science send', title: 'From a start point, one send of N flasks' },
];

const START_UNITS: SegmentedOption<StartUnit>[] = [
  { value: 'mutagen', label: 'Mutagen' },
  { value: 'evo', label: 'Evo' },
];

/**
 * Set the range in one go, four ways. Hidden by default — the pane above does
 * the same job inline — and seeded from the current selection when opened.
 */
function SelectionControls({ summary }: { summary: RangeSummary }) {
  const { ruleset, params, difficulty, setSelection, ensureVisible } = useLab();

  const [mode, setMode] = useState<Mode>('mutagen');
  const [startUnit, setStartUnit] = useState<StartUnit>('evo');
  const [startMutagen, setStartMutagen] = useState(() =>
    round2(toDisplayMutagen(summary.from.mutagen)),
  );
  const [startEvo, setStartEvo] = useState(() => round2(summary.from.evo * 100));
  const [endMutagen, setEndMutagen] = useState(() => round2(toDisplayMutagen(summary.to.mutagen)));
  const [endEvo, setEndEvo] = useState(() => round2(summary.to.evo * 100));
  const [threatTarget, setThreatTarget] = useState(() =>
    Math.round(summary.instantThreat) || 500_000,
  );
  const [science, setScience] = useState<ScienceKey>('space');
  const [flasks, setFlasks] = useState(() =>
    Math.round(mutagenToFlasks(summary.mutagen, 'space', difficulty)) || 1000,
  );

  // A branch without instant threat has no threat target to solve for.
  const modes = ruleset.hasInstantThreat ? MODES : MODES.filter((entry) => entry.value !== 'threat');
  const activeMode = modes.some((entry) => entry.value === mode) ? mode : 'mutagen';

  // The two range modes name their own unit; the two send modes let you pick.
  const unit: StartUnit =
    activeMode === 'mutagen' ? 'mutagen' : activeMode === 'evo' ? 'evo' : startUnit;

  const apply = (event: FormEvent) => {
    event.preventDefault();

    const from =
      unit === 'mutagen'
        ? fromDisplayMutagen(startMutagen)
        : mutagenToReachEvo(ruleset, startEvo / 100, 0);

    let to: number;
    switch (activeMode) {
      case 'mutagen':
        to = fromDisplayMutagen(endMutagen);
        break;
      case 'evo':
        to = mutagenToReachEvo(ruleset, endEvo / 100, 0);
        break;
      case 'threat':
        to = mutagenForThreat(from, threatTarget, params);
        break;
      case 'science':
        to = from + SCIENCE[science].value * difficulty * flasks;
        break;
    }

    if (!(to >= from)) return;
    ensureVisible(to);
    setSelection([from, to]);
  };

  return (
    <form className="selection-controls" onSubmit={apply}>
      <SegmentedControl value={activeMode} onChange={setMode} options={modes} />

      <div className="control-fields">
        {activeMode === 'threat' || activeMode === 'science' ? (
          <Field label="Start in">
            <SegmentedControl value={startUnit} onChange={setStartUnit} options={START_UNITS} />
          </Field>
        ) : null}

        {unit === 'mutagen' ? (
          <Field label="From — mutagen">
            <NumberField
              value={startMutagen}
              onChange={setStartMutagen}
              min={0}
              step={10_000}
              wide
            />
          </Field>
        ) : (
          <Field label="From — evo">
            <NumberField
              value={startEvo}
              onChange={setStartEvo}
              min={0}
              max={1200}
              step={5}
              suffix="%"
            />
          </Field>
        )}

        {activeMode === 'mutagen' ? (
          <Field label="To — mutagen">
            <NumberField value={endMutagen} onChange={setEndMutagen} min={0} step={10_000} wide />
          </Field>
        ) : null}

        {activeMode === 'evo' ? (
          <Field label="To — evo">
            <NumberField
              value={endEvo}
              onChange={setEndEvo}
              min={0}
              max={1200}
              step={5}
              suffix="%"
            />
          </Field>
        ) : null}

        {activeMode === 'threat' ? (
          <Field label="Instant threat">
            <NumberField
              value={threatTarget}
              onChange={setThreatTarget}
              min={0}
              step={50_000}
              wide
            />
          </Field>
        ) : null}

        {activeMode === 'science' ? (
          <>
            <Field label="Science">
              <select
                value={science}
                onChange={(event) => setScience(event.target.value as ScienceKey)}
              >
                {SCIENCE_ORDER.map((key) => (
                  <option key={key} value={key}>
                    {SCIENCE[key].colour}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Flasks">
              <NumberField value={flasks} onChange={setFlasks} min={0} step={100} wide />
            </Field>
          </>
        ) : null}

        <button type="submit" className="primary">
          select
        </button>
      </div>
    </form>
  );
}

const round2 = (value: number) => Number(value.toFixed(2));

/* --------------------------------------------------------------- primitives */

/** "a ⟶ b (+c)" for a range, or just "b" for a point selection. */
function Span({
  isRange,
  from,
  to,
  delta,
}: {
  isRange: boolean;
  from: ReactNode;
  to: ReactNode;
  delta?: string;
}) {
  return isRange ? <Transition from={from} to={to} delta={delta} /> : <>{to}</>;
}

function Group({
  title,
  control,
  children,
}: {
  title: string;
  /** Rendered flush right in the group heading. */
  control?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="sel-group">
      <span className="sel-group-title">
        <span className="sel-group-name">{title}</span>
        {control}
      </span>
      {children}
    </div>
  );
}

function Row({
  label,
  value,
  strong,
  tone,
}: {
  label: string;
  value: ReactNode;
  strong?: boolean;
  tone?: 'warn';
}) {
  return (
    <div className={`sel-row${strong ? ' strong' : ''}${tone ? ` tone-${tone}` : ''}`}>
      <span className="sel-label">{label}</span>
      <span className="sel-value">{value}</span>
    </div>
  );
}

/** Segments wide enough to hold a number get one; the rest rely on the legend. */
const MIN_LABELLED_FRACTION = 0.11;

function CompositionBar({ label, composition }: { label: string; composition: TierWeights }) {
  return (
    <div className="mix">
      <span className="mix-label">{label}</span>
      <div className="comp-bar">
        {UNIT_TIERS.map((tier) => {
          const fraction = composition[tier];
          if (fraction <= 0.0005) return null;
          return (
            <span
              key={tier}
              style={{ width: `${fraction * 100}%`, background: TIER_COLOURS[tier] }}
              title={`${tier}: ${formatPercent(fraction)}`}
            >
              {fraction >= MIN_LABELLED_FRACTION ? Math.round(fraction * 100) : ''}
            </span>
          );
        })}
      </div>
    </div>
  );
}
